import React, { useMemo, useState } from 'react';
import AlertModal from '../modals/AlertModal';

interface Props {
  children: any;
}

export type AlertTypes = 'Success' | 'Error' | 'Info' | 'Warning';

export interface AlertContextInterface {
  error: (info: AlertInfoInterface) => void;
}

export interface AlertInfoInterface {
  type?: AlertTypes;
  title?: string;
  description?: string;
  footer?: JSX.Element;
}

export const AlertContext = React.createContext<AlertContextInterface>(null);

export default function AlertModalProvider(props: Props): JSX.Element {
  const { children } = props;

  const [alertInfo, setAlertInfo] = useState<AlertInfoInterface>({
    title: '',
    description: '',
    footer: null,
  });

  const [showAlertModal, setShowAlertModal] = useState(false);

  function toggleAlertModal(info?: AlertInfoInterface) {
    if (info) {
      setShowAlertModal(true);
      setAlertInfo(info);

      return;
    }

    setShowAlertModal(false);
    setAlertInfo({
      title: '',
      description: '',
      footer: null,
    });
  }

  const renderAlertModal = useMemo(() => {
    return (
      <AlertModal
        openModal={showAlertModal}
        title={alertInfo?.title}
        description={alertInfo?.description}
        footer={alertInfo?.footer}
        toggleModal={() => toggleAlertModal(null)}
      />
    );
  }, [showAlertModal, alertInfo]);

  const context: AlertContextInterface = useMemo(
    () => ({
      error: (info: AlertInfoInterface) => {
        toggleAlertModal({ ...info, type: 'Error' });
      },
    }),
    [],
  );

  return (
    <AlertContext.Provider value={context}>
      {children}
      {renderAlertModal}
    </AlertContext.Provider>
  );
}
